import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Copy, Check, Code, Mic, Palette, Monitor, Key, AlertCircle, Image, Zap } from "lucide-react";
import { toast } from "sonner";
import { useWorkspaceApiKeys } from "@/hooks/use-workspace-api-keys";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Link } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SUPABASE_URL } from "@/integrations/supabase/client";

interface WebWidgetSettings {
  enabled: boolean;
  theme: "light" | "dark" | "auto";
  primaryColor: string;
  position: "bottom-right" | "bottom-left";
  buttonSize: "small" | "medium" | "large";
  showBranding: boolean;
  publicApiKey?: string;
  avatarUrl?: string;
  avatarType: "default" | "custom";
  integrationMode: "floating" | "headless";
}

interface WebWidgetConfigProps {
  agentId: string;
  workspaceId: string;
  settings: WebWidgetSettings;
  onChange?: (settings: WebWidgetSettings) => void;
}

const COLOR_PRESETS = [
  { name: "Blue", value: "#3B82F6" },
  { name: "Purple", value: "#8B5CF6" },
  { name: "Green", value: "#10B981" },
  { name: "Red", value: "#EF4444" },
  { name: "Orange", value: "#F97316" },
  { name: "Pink", value: "#EC4899" },
];

export function WebWidgetConfig({
  agentId,
  workspaceId,
  settings,
  onChange,
}: WebWidgetConfigProps) {
  // Add defaults for new properties
  const normalizedSettings: WebWidgetSettings = {
    ...settings,
    avatarType: settings.avatarType || "default",
    avatarUrl: settings.avatarUrl || "",
    integrationMode: settings.integrationMode || "floating",
  };

  const [localSettings, setLocalSettings] = useState<WebWidgetSettings>(normalizedSettings);
  const [copied, setCopied] = useState(false);
  const [copiedHeadless, setCopiedHeadless] = useState(false);
  const { data: apiKeys } = useWorkspaceApiKeys(workspaceId);
  
  // Filter only public API keys
  const publicKeys = apiKeys?.filter(key => key.key_type === 'public' && key.is_active) || [];

  const handleChange = (newSettings: WebWidgetSettings) => {
    setLocalSettings(newSettings);
    onChange?.(newSettings); // Sync back to parent
  };

  // Generate embed code for floating button mode
  const floatingEmbedCode = localSettings.publicApiKey 
    ? `<!-- Verbeo Voice Widget -->
<script>
  window.VerbeoWidgetConfig = {
    apiKey: "${localSettings.publicApiKey}",
    agentId: "${agentId}",
    theme: "${localSettings.theme}",
    primaryColor: "${localSettings.primaryColor}",
    position: "${localSettings.position}",
    buttonSize: "${localSettings.buttonSize}",
    showBranding: ${localSettings.showBranding}${localSettings.avatarType === "custom" && localSettings.avatarUrl ? `,
    avatarUrl: "${localSettings.avatarUrl}"` : ""}
  };
</script>
<script src="${SUPABASE_URL}/functions/v1/widget-script" async></script>`
    : `<!-- Verbeo Voice Widget -->
<!-- ⚠️ You must paste your full Public API Key below -->
<script>
  window.VerbeoWidgetConfig = {
    apiKey: "PASTE_YOUR_FULL_PUBLIC_API_KEY_HERE",
    agentId: "${agentId}",
    theme: "${localSettings.theme}",
    primaryColor: "${localSettings.primaryColor}",
    position: "${localSettings.position}",
    buttonSize: "${localSettings.buttonSize}",
    showBranding: ${localSettings.showBranding}${localSettings.avatarType === "custom" && localSettings.avatarUrl ? `,
    avatarUrl: "${localSettings.avatarUrl}"` : ""}
  };
</script>
<script src="${SUPABASE_URL}/functions/v1/widget-script" async></script>`;

  // Generate embed code for headless mode
  const headlessEmbedCode = localSettings.publicApiKey 
    ? `<!-- Verbeo Voice Widget (Headless Mode) -->
<script>
  window.VerbeoWidgetConfig = {
    apiKey: "${localSettings.publicApiKey}",
    agentId: "${agentId}",
    headless: true
  };
</script>
<script src="${SUPABASE_URL}/functions/v1/widget-script" async></script>

<!-- Your custom button (Option 1: Simple data attribute) -->
<button data-verbeo-trigger>Talk to AI</button>

<!-- OR Option 2: Use the JavaScript API -->
<script>
  // Wait for widget to load
  document.addEventListener('DOMContentLoaded', () => {
    // Start call
    window.verbeoWidget.start();
    
    // End call
    window.verbeoWidget.stop();
    
    // Check if connected
    window.verbeoWidget.isConnected();
    
    // Mute/unmute
    window.verbeoWidget.mute();
    window.verbeoWidget.unmute();
    
    // Listen to events
    window.verbeoWidget.on('call-start', (data) => console.log('Call started'));
    window.verbeoWidget.on('call-end', (data) => console.log('Call ended'));
    window.verbeoWidget.on('connecting', () => console.log('Connecting...'));
    window.verbeoWidget.on('connected', () => console.log('Connected!'));
    window.verbeoWidget.on('error', (data) => console.log('Error:', data.message));
  });
</script>`
    : `<!-- Verbeo Voice Widget (Headless Mode) -->
<!-- ⚠️ You must paste your full Public API Key below -->
<script>
  window.VerbeoWidgetConfig = {
    apiKey: "PASTE_YOUR_FULL_PUBLIC_API_KEY_HERE",
    agentId: "${agentId}",
    headless: true
  };
</script>
<script src="${SUPABASE_URL}/functions/v1/widget-script" async></script>

<!-- Your custom button -->
<button data-verbeo-trigger>Talk to AI</button>`;

  const embedCode = localSettings.integrationMode === "headless" ? headlessEmbedCode : floatingEmbedCode;

  const copyEmbedCode = () => {
    navigator.clipboard.writeText(embedCode);
    if (localSettings.integrationMode === "headless") {
      setCopiedHeadless(true);
      setTimeout(() => setCopiedHeadless(false), 2000);
    } else {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
    toast.success("Embed code copied to clipboard");
  };

  const isCopied = localSettings.integrationMode === "headless" ? copiedHeadless : copied;

  return (
    <div className="space-y-6">
      {/* Enable Widget */}
      <div className="flex items-center justify-between">
        <div className="space-y-0.5">
          <Label className="text-base">Enable Web Widget</Label>
          <p className="text-sm text-muted-foreground">
            Allow embedding a voice widget on external websites
          </p>
        </div>
        <Switch
          checked={localSettings.enabled}
          onCheckedChange={(enabled) => handleChange({ ...localSettings, enabled })}
        />
      </div>

      {localSettings.enabled && (
        <>
          {/* Public API Key Selection */}
          <div className="space-y-4 pt-4 border-t">
            <div className="flex items-center gap-2">
              <Key className="w-4 h-4 text-muted-foreground" />
              <Label className="font-medium">Public API Key</Label>
            </div>
            
            {publicKeys.length === 0 ? (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="flex items-center justify-between">
                  <span>You need a public API key for the widget to work.</span>
                  <Link to={`/${workspaceId}/settings?tab=api-keys`}>
                    <Button variant="outline" size="sm">
                      Create Public Key
                    </Button>
                  </Link>
                </AlertDescription>
              </Alert>
            ) : (
              <div className="space-y-2">
                <Input
                  type="text"
                  value={localSettings.publicApiKey || ""}
                  onChange={(e) => handleChange({ ...localSettings, publicApiKey: e.target.value })}
                  placeholder="Paste your full public API key (vbr_pub_...)"
                  className="font-mono text-sm"
                />
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">
                    Available public keys: {publicKeys.map(k => k.name).join(', ')}
                  </p>
                  <Link 
                    to={`/${workspaceId}/settings?tab=api-keys`}
                    className="text-xs text-primary hover:underline"
                  >
                    Manage API Keys →
                  </Link>
                </div>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Paste your <strong>full</strong> public API key (starts with <code className="bg-muted px-1 rounded">vbr_pub_</code>). You received this when you created the key.{' '}
              <Link to={`/${workspaceId}/settings?tab=api-keys`} className="text-primary hover:underline">
                Go to API Keys
              </Link>
            </p>
          </div>

          {/* Integration Mode */}
          <div className="space-y-4 pt-4 border-t">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-muted-foreground" />
              <Label className="font-medium">Integration Mode</Label>
            </div>
            
            <Tabs
              value={localSettings.integrationMode}
              onValueChange={(value: "floating" | "headless") => handleChange({ ...localSettings, integrationMode: value })}
              className="w-full"
            >
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="floating">Floating Button</TabsTrigger>
                <TabsTrigger value="headless">Custom Button (Headless)</TabsTrigger>
              </TabsList>
              
              <TabsContent value="floating" className="mt-4 space-y-4">
                <p className="text-sm text-muted-foreground">
                  A floating button appears on your website. Visitors click it to open the voice widget.
                </p>
              </TabsContent>
              
              <TabsContent value="headless" className="mt-4 space-y-4">
                <Alert>
                  <Zap className="h-4 w-4" />
                  <AlertDescription>
                    <strong>Headless Mode:</strong> No UI is rendered. You control when to start/stop calls using your own buttons and our JavaScript API.
                  </AlertDescription>
                </Alert>
                
                <div className="space-y-3 text-sm">
                  <p className="font-medium">Available API Methods:</p>
                  <div className="grid gap-2 text-xs">
                    <div className="flex gap-2">
                      <code className="bg-muted px-2 py-1 rounded">verbeoWidget.start()</code>
                      <span className="text-muted-foreground">Start a voice call</span>
                    </div>
                    <div className="flex gap-2">
                      <code className="bg-muted px-2 py-1 rounded">verbeoWidget.stop()</code>
                      <span className="text-muted-foreground">End the call</span>
                    </div>
                    <div className="flex gap-2">
                      <code className="bg-muted px-2 py-1 rounded">verbeoWidget.isConnected()</code>
                      <span className="text-muted-foreground">Check connection status</span>
                    </div>
                    <div className="flex gap-2">
                      <code className="bg-muted px-2 py-1 rounded">verbeoWidget.mute() / unmute()</code>
                      <span className="text-muted-foreground">Toggle microphone</span>
                    </div>
                    <div className="flex gap-2">
                      <code className="bg-muted px-2 py-1 rounded">verbeoWidget.on(event, callback)</code>
                      <span className="text-muted-foreground">Listen to events</span>
                    </div>
                  </div>
                  
                  <p className="font-medium mt-4">Simple Integration:</p>
                  <p className="text-muted-foreground">
                    Add <code className="bg-muted px-1 rounded">data-verbeo-trigger</code> to any button to make it start a call on click.
                  </p>
                </div>
              </TabsContent>
            </Tabs>
          </div>

          {/* Only show appearance settings for floating mode */}
          {localSettings.integrationMode === "floating" && (
            <>
              {/* Avatar Section */}
              <div className="space-y-4 pt-4 border-t">
                <div className="flex items-center gap-2">
                  <Image className="w-4 h-4 text-muted-foreground" />
                  <Label className="font-medium">Widget Avatar</Label>
                </div>

                <RadioGroup
                  value={localSettings.avatarType}
                  onValueChange={(value: "default" | "custom") =>
                    handleChange({ ...localSettings, avatarType: value })
                  }
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="default" id="avatar-default" />
                    <Label htmlFor="avatar-default" className="font-normal cursor-pointer">
                      Default Icon
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="custom" id="avatar-custom" />
                    <Label htmlFor="avatar-custom" className="font-normal cursor-pointer">
                      Custom Image
                    </Label>
                  </div>
                </RadioGroup>

                {localSettings.avatarType === "custom" && (
                  <div className="space-y-2">
                    <Input
                      type="url"
                      value={localSettings.avatarUrl || ""}
                      onChange={(e) => handleChange({ ...localSettings, avatarUrl: e.target.value })}
                      placeholder="https://example.com/avatar.png"
                      className="text-sm"
                    />
                    <p className="text-xs text-muted-foreground">
                      Enter a URL to an image (PNG, JPG, or SVG). Square images work best.
                    </p>
                  </div>
                )}
              </div>

              {/* Appearance Section */}
              <div className="space-y-4 pt-4 border-t">
                <div className="flex items-center gap-2">
                  <Palette className="w-4 h-4 text-muted-foreground" />
                  <Label className="font-medium">Appearance</Label>
                </div>

                {/* Theme */}
                <div className="space-y-2">
                  <Label className="text-sm">Theme</Label>
                  <RadioGroup
                    value={localSettings.theme}
                    onValueChange={(value: "light" | "dark" | "auto") =>
                      handleChange({ ...localSettings, theme: value })
                    }
                    className="flex gap-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="light" id="theme-light" />
                      <Label htmlFor="theme-light" className="font-normal cursor-pointer">
                        Light
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="dark" id="theme-dark" />
                      <Label htmlFor="theme-dark" className="font-normal cursor-pointer">
                        Dark
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="auto" id="theme-auto" />
                      <Label htmlFor="theme-auto" className="font-normal cursor-pointer">
                        Auto (System)
                      </Label>
                    </div>
                  </RadioGroup>
                </div>

                {/* Primary Color */}
                <div className="space-y-2">
                  <Label className="text-sm">Primary Color</Label>
                  <div className="flex items-center gap-3">
                    <div className="flex gap-2">
                      {COLOR_PRESETS.map((color) => (
                        <button
                          key={color.value}
                          type="button"
                          className={`w-8 h-8 rounded-full border-2 transition-all ${
                            localSettings.primaryColor === color.value
                              ? "border-foreground scale-110"
                              : "border-transparent hover:scale-105"
                          }`}
                          style={{ backgroundColor: color.value }}
                          onClick={() => handleChange({ ...localSettings, primaryColor: color.value })}
                          title={color.name}
                        />
                      ))}
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        type="color"
                        value={localSettings.primaryColor}
                        onChange={(e) => handleChange({ ...localSettings, primaryColor: e.target.value })}
                        className="w-10 h-8 p-1 cursor-pointer"
                      />
                      <Input
                        type="text"
                        value={localSettings.primaryColor}
                        onChange={(e) => handleChange({ ...localSettings, primaryColor: e.target.value })}
                        className="w-24 font-mono text-sm"
                        placeholder="#000000"
                      />
                    </div>
                  </div>
                </div>

                {/* Button Size */}
                <div className="space-y-2">
                  <Label className="text-sm">Button Size</Label>
                  <Select
                    value={localSettings.buttonSize}
                    onValueChange={(value: "small" | "medium" | "large") =>
                      handleChange({ ...localSettings, buttonSize: value })
                    }
                  >
                    <SelectTrigger className="w-[180px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="small">Small (48px)</SelectItem>
                      <SelectItem value="medium">Medium (56px)</SelectItem>
                      <SelectItem value="large">Large (64px)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Position Section */}
              <div className="space-y-4 pt-4 border-t">
                <div className="flex items-center gap-2">
                  <Monitor className="w-4 h-4 text-muted-foreground" />
                  <Label className="font-medium">Position</Label>
                </div>

                <RadioGroup
                  value={localSettings.position}
                  onValueChange={(value: "bottom-right" | "bottom-left") =>
                    handleChange({ ...localSettings, position: value })
                  }
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="bottom-right" id="pos-br" />
                    <Label htmlFor="pos-br" className="font-normal cursor-pointer">
                      Bottom Right
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="bottom-left" id="pos-bl" />
                    <Label htmlFor="pos-bl" className="font-normal cursor-pointer">
                      Bottom Left
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Branding */}
              <div className="flex items-center justify-between pt-4 border-t">
                <div className="space-y-0.5">
                  <Label>Show Branding</Label>
                  <p className="text-sm text-muted-foreground">
                    Display "Powered by Verbeo" in the widget
                  </p>
                </div>
                <Switch
                  checked={localSettings.showBranding}
                  onCheckedChange={(showBranding) =>
                    handleChange({ ...localSettings, showBranding })
                  }
                />
              </div>

              {/* Widget Preview */}
              <div className="pt-4 border-t">
                <div className="flex items-center gap-2 mb-3">
                  <Mic className="w-4 h-4 text-muted-foreground" />
                  <Label className="font-medium">Widget Preview</Label>
                </div>
                <div 
                  className={`relative w-full h-48 rounded-lg border overflow-hidden ${
                    localSettings.theme === "dark" ? "bg-gray-900" : "bg-gray-100"
                  }`}
                >
                  <div 
                    className={`absolute ${
                      localSettings.position === "bottom-right" ? "right-4" : "left-4"
                    } bottom-4`}
                  >
                    <div
                      className="rounded-full flex items-center justify-center shadow-lg cursor-pointer transition-transform hover:scale-105 overflow-hidden"
                      style={{
                        backgroundColor: localSettings.avatarType === "custom" && localSettings.avatarUrl ? "transparent" : localSettings.primaryColor,
                        width: localSettings.buttonSize === "small" ? 48 : localSettings.buttonSize === "medium" ? 56 : 64,
                        height: localSettings.buttonSize === "small" ? 48 : localSettings.buttonSize === "medium" ? 56 : 64,
                      }}
                    >
                      {localSettings.avatarType === "custom" && localSettings.avatarUrl ? (
                        <img 
                          src={localSettings.avatarUrl} 
                          alt="Widget avatar" 
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      ) : (
                        <Mic className="w-6 h-6 text-white" />
                      )}
                    </div>
                  </div>
                  {localSettings.showBranding && (
                    <div 
                      className={`absolute ${
                        localSettings.position === "bottom-right" ? "right-4" : "left-4"
                      } bottom-20 text-xs ${
                        localSettings.theme === "dark" ? "text-gray-500" : "text-gray-400"
                      }`}
                    >
                      Powered by Verbeo
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Embed Code */}
          <div className="pt-4 border-t">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Code className="w-4 h-4 text-muted-foreground" />
                <Label className="font-medium">
                  Embed Code {localSettings.integrationMode === "headless" && "(Headless)"}
                </Label>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={copyEmbedCode}
                className="gap-2"
              >
                {isCopied ? (
                  <>
                    <Check className="w-4 h-4" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Copy Code
                  </>
                )}
              </Button>
            </div>
            <div className="relative">
              <pre className="p-4 bg-muted rounded-lg overflow-x-auto text-xs font-mono max-h-80">
                <code>{embedCode}</code>
              </pre>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {localSettings.integrationMode === "headless" 
                ? "Add this code to your website, then use the API methods or data-verbeo-trigger attribute to start calls."
                : <>Paste this code before the closing <code className="bg-muted px-1 rounded">&lt;/body&gt;</code> tag on your website.</>
              }
            </p>
          </div>
        </>
      )}
    </div>
  );
}
